/**
 * Merge fields for admin emails: {{first_name}}, {{dojo}}, {{membership_end_date}},
 * {{member_number}}. An identical copy lives in api/src/lib/merge.ts (the
 * worker that sends); keep the two the same, and their tests too.
 */

export const MERGE_FIELDS = [
  { key: 'first_name', label: 'First name', sample: 'Sam' },
  { key: 'dojo', label: 'Dojo', sample: 'Eastleigh' },
  { key: 'membership_end_date', label: 'Membership end date', sample: '31 May 2027' },
  { key: 'member_number', label: 'Member number', sample: '4021' },
] as const;
export type MergeKey = (typeof MERGE_FIELDS)[number]['key'];
export type MergeValues = Record<MergeKey, string>;

const day = (iso: string) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso.slice(0, 10)}T00:00:00Z`));

/** One recipient's values, with the wording used when something is not known. */
export function mergeValues(r: { first_name: string | null; dojo: string | null; member_until: string | null; member_number: number | string | null }): MergeValues {
  return {
    first_name: r.first_name?.trim() || 'there',
    dojo: r.dojo?.trim() || 'your dojo',
    membership_end_date: r.member_until ? day(r.member_until) : 'the end of your membership',
    member_number: r.member_number == null || r.member_number === '' ? 'not yet issued' : String(r.member_number),
  };
}

const TOKEN = /\{\{\s*([a-z_]+)\s*\}\}/g;
const KEYS = new Set<string>(MERGE_FIELDS.map(f => f.key));

/** Fills in known fields. Unknown ones are left as typed, so a mistake shows in the preview. */
export function renderMerge(text: string, values: Partial<Record<MergeKey, string>>): string {
  return text.replace(TOKEN, (whole, key: string) => (KEYS.has(key) && values[key as MergeKey] !== undefined ? values[key as MergeKey]! : whole));
}

/** Fields typed that do not exist, e.g. {{firstname}}. */
export function unknownTokens(text: string): string[] {
  return [...new Set([...text.matchAll(TOKEN)].map(m => m[1]!).filter(k => !KEYS.has(k)))];
}
